import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PaybackCalculator } from "@/components/calculators/PaybackCalculator";
import { SubsidyGuide2025 } from "@/components/guides/SubsidyGuide2025";
import { InstallationChecklist } from "@/components/guides/InstallationChecklist";
import { VideoTestimonials } from "@/components/video/VideoTestimonials";
import { BehindTheScenes } from "@/components/video/BehindTheScenes";
import { useScrollTracking } from "@/hooks/useScrollTracking";
import { Calculator, BookOpen, FileText, Video, Users } from "lucide-react";

export default function KnowledgeHub() {
  useScrollTracking();

  return (
    <>
      <SEO 
        title="Kennisbank - Alles over Duurzame Energie | SOL-VI" 
        description="Complete kennisbank met calculators, gidsen, checklists en video's over zonnepanelen, warmtepompen en duurzame energie. Expert advies van SOL-VI." 
        type="website" 
      />
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-primary/5 to-background py-16">
          <div className="container mx-auto px-4">
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl font-bold">
                SOL-VI Kennisbank
              </h1>
              <p className="text-xl text-muted-foreground">
                Alles wat u moet weten over duurzame energie. Van calculators tot praktische gidsen, 
                video testimonials en installatie-inzichten.
              </p>
            </div>
          </div>
        </section>

        {/* Knowledge Hub Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <Tabs defaultValue="calculators" className="space-y-8">
              <TabsList className="grid w-full grid-cols-5 lg:w-fit lg:mx-auto">
                <TabsTrigger value="calculators" className="flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  <span className="hidden sm:inline">Calculators</span>
                </TabsTrigger>
                <TabsTrigger value="guides" className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="hidden sm:inline">Gidsen</span>
                </TabsTrigger>
                <TabsTrigger value="checklists" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">Checklists</span>
                </TabsTrigger>
                <TabsTrigger value="testimonials" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Verhalen</span>
                </TabsTrigger>
                <TabsTrigger value="videos" className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  <span className="hidden sm:inline">Video's</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="calculators" className="space-y-8">
                <div className="text-center space-y-2 mb-8">
                  <h2 className="text-3xl font-bold">Bereken uw voordeel</h2>
                  <p className="text-muted-foreground">
                    Ontdek de terugverdientijd en besparing van verschillende duurzame oplossingen
                  </p>
                </div>
                <PaybackCalculator />
              </TabsContent>

              <TabsContent value="guides" className="space-y-8">
                <div className="text-center space-y-2 mb-8">
                  <h2 className="text-3xl font-bold">Subsidiegids 2025</h2>
                  <p className="text-muted-foreground">
                    Actuele informatie over alle beschikbare subsidies en belastingvoordelen
                  </p>
                </div>
                <SubsidyGuide2025 />
              </TabsContent>

              <TabsContent value="checklists" className="space-y-8">
                <div className="text-center space-y-2 mb-8">
                  <h2 className="text-3xl font-bold">Installatie Checklists</h2>
                  <p className="text-muted-foreground">
                    Praktische stap-voor-stap gidsen voor een succesvolle installatie
                  </p>
                </div>
                <InstallationChecklist />
              </TabsContent>

              <TabsContent value="testimonials" className="space-y-8">
                <div className="text-center space-y-2 mb-8">
                  <h2 className="text-3xl font-bold">Klant Verhalen</h2>
                  <p className="text-muted-foreground">
                    Echte ervaringen van tevreden klanten in uw regio
                  </p>
                </div>
                <VideoTestimonials />
              </TabsContent>

              <TabsContent value="videos" className="space-y-8">
                <div className="text-center space-y-2 mb-8">
                  <h2 className="text-3xl font-bold">Behind the Scenes</h2>
                  <p className="text-muted-foreground">
                    Kijk mee achter de schermen bij onze installaties en werkwijze
                  </p>
                </div>
                <BehindTheScenes />
              </TabsContent>
            </Tabs>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
}